import { fabric } from 'fabric';
import { IEvent } from 'fabric/fabric-impl';
import { useCallback, useEffect, useState } from 'react';
import Vec2 from 'vec2';

import {
  CANVAS_CURSOR,
  CANVAS_OBJECT_TYPE_PREFIX,
  DEFAULT_DEPTH,
  FREE_DRAWING_SHAPE_MINIMUM_LENGTH,
  MEASUREMENT_STROKE_WIDTH,
} from '~/core/constants';
import { DrawingTabEnum } from '~/core/enums/drawing-tab.enum';
import {
  SimpleLine,
  freeDrawerRealTimeLength,
  lineContainsPoint,
  pointsAreEqual,
} from '~/core/helpers';
import { makeGetNewPoint } from '~/core/utils';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectFabricCanvas,
  updateActiveFreeDrawing,
} from '~/redux/slices/fabric';
import {
  selectSelectedDrawingTab,
  updateSelectedDrawingTab,
} from '~/redux/slices/shape';

export const useFreeDrawer = () => {
  const dispatch = useAppDispatch();

  const canvas = useAppSelector(selectFabricCanvas);
  const [mouseDownState, setMouseDownState] = useState(false);
  const [points, setPoints] = useState<Array<Vec2>>([]);

  const getNewPoint = makeGetNewPoint(points);
  const selectedDrawingTab = useAppSelector(selectSelectedDrawingTab);
  const isFreeDrawingState =
    selectedDrawingTab === DrawingTabEnum.FREE_DRAWING_TAB;

  const mouseDown = useCallback(
    (evt: IEvent<Event>) => {
      if (!isFreeDrawingState) {
        return;
      }
      fabric.Object.prototype.selectable = false;
      canvas.selection = false;

      const pt = canvas.getPointer(evt as unknown as Event);

      setPoints([new Vec2(pt.x, pt.y)]);
      setMouseDownState(true);
    },
    [canvas, isFreeDrawingState]
  );

  const getFreeDrawPointsToRemove = (deDupe: Vec2[]): number[] => {
    const removals: number[] = [];

    for (let i = 0; i < deDupe.length - 2; i++) {
      const [a, b, c] = [deDupe[i], deDupe[i + 1], deDupe[i + 2]];

      // if next three points are all horizontal or vertical and in the same line, remove the middle one
      if (
        ((a.x === b.x && b.x === c.x) || (a.y === b.y && b.y === c.y)) &&
        lineContainsPoint(new SimpleLine(a, c), b, true, true)
      ) {
        removals.push(i + 1);
      }
    }

    return removals;
  };

  const cleanFreeDrawPoints = useCallback((points: Vec2[]): Vec2[] => {
    if (points.length < 3) {
      return points;
    }
    let deDupe = points.filter((point, index, array) => {
      if (index === 0) {
        return true;
      }
      if (index === array.length - 1 && pointsAreEqual(array[0], point)) {
        return false;
      }

      return !(index > 0 && pointsAreEqual(array[index - 1], point));
    });

    while (deDupe) {
      // started empty or reduced to a single point, it's an invalid skeleton
      if (deDupe.length < 3) {
        return deDupe;
      }

      const removals: number[] = getFreeDrawPointsToRemove(deDupe);

      if (!removals.length) {
        return deDupe;
      }

      // keep the points that are _not_ in the removals array
      deDupe = deDupe.filter(
        (_value, dedupeIndex) => !removals.includes(dedupeIndex)
      );
    }

    return [];
  }, []);

  const mouseMove = useCallback(
    (evt: IEvent<Event>) => {
      if (!isFreeDrawingState || !mouseDownState) {
        return;
      }
      const pt = canvas.getPointer(evt as unknown as Event);
      const l = points.length;
      let newPt = getNewPoint(l - 1, new Vec2(pt));

      if (newPt) {
        const newPoints = cleanFreeDrawPoints([...points, newPt]);

        setPoints(newPoints);
      } else if (l > 1) {
        newPt = getNewPoint(l - 2, new Vec2(pt));
        if (newPt) {
          const newPoints = cleanFreeDrawPoints([
            ...points.slice(0, l - 1),
            newPt,
          ]);

          setPoints(newPoints);
        }
      }
    },
    [
      canvas,
      cleanFreeDrawPoints,
      getNewPoint,
      isFreeDrawingState,
      mouseDownState,
      points,
    ]
  );

  const mouseUp = useCallback(() => {
    setMouseDownState(false);
    if (!isFreeDrawingState) {
      return;
    }

    clearFreeDrawFromCanvas(canvas);

    fabric.Object.prototype.selectable = true; //make everything else selectable

    const lengthInfo = freeDrawerRealTimeLength(points);

    if (
      points.length > 1 &&
      lengthInfo.length >= FREE_DRAWING_SHAPE_MINIMUM_LENGTH
    ) {
      dispatch(updateActiveFreeDrawing(points));
    } else {
      dispatch(updateActiveFreeDrawing([]));
    }
  }, [canvas, dispatch, isFreeDrawingState, points]);

  const activateWithSelectedTab = (drawingTab: DrawingTabEnum) => {
    dispatch(updateSelectedDrawingTab(drawingTab));
    setPoints([]);
  };

  const clearFreeDrawFromCanvas = (canvas: fabric.Canvas) => {
    if (!canvas) {
      return;
    }
    canvas.getObjects().forEach((obj: fabric.Object) => {
      if (
        obj.name === CANVAS_OBJECT_TYPE_PREFIX.FREE_DRAWING_TEMP_LENGTH ||
        obj.name === CANVAS_OBJECT_TYPE_PREFIX.FREE_DRAWING_TEMP_SHAPE
      ) {
        canvas.remove(obj);
      }
    });
    canvas.renderAll();
  };

  useEffect(() => {
    if (!canvas) {
      return undefined;
    }
    canvas.on('mouse:down', mouseDown);
    canvas.on('mouse:move', mouseMove);
    canvas.on('mouse:up', mouseUp);

    return () => {
      canvas.off('mouse:down', mouseDown);
      canvas.off('mouse:move', mouseMove);
      canvas.off('mouse:up', mouseUp);
    };
  }, [canvas, mouseDown, mouseMove, mouseUp]);

  useEffect(() => {
    // checking mouseDownState will not allow you to start another freedraw until the previous is really complete
    if (canvas && isFreeDrawingState && points.length > 1 && mouseDownState) {
      clearFreeDrawFromCanvas(canvas);

      const opt = {
        stroke: 'grey',
        strokeWidth: DEFAULT_DEPTH,
        fill: 'transparent',
        name: CANVAS_OBJECT_TYPE_PREFIX.FREE_DRAWING_TEMP_SHAPE,
      };
      const freeShape = new fabric.Polyline(points, opt);
      const lengthInfo = freeDrawerRealTimeLength(points);
      const lengthObj = new fabric.Text(
        `Total Length: ${lengthInfo.length.toString()} in.`,
        {
          left: lengthInfo.pos.x,
          top: lengthInfo.pos.y,
          stroke: 'grey',
          strokeWidth: MEASUREMENT_STROKE_WIDTH,
          fontSize: 3,
          name: CANVAS_OBJECT_TYPE_PREFIX.FREE_DRAWING_TEMP_LENGTH,
          data: {
            value: lengthInfo.length,
          },
        }
      );

      canvas.add(freeShape);
      canvas.add(lengthObj);
      canvas.renderAll();
    }
  }, [canvas, isFreeDrawingState, points, mouseDownState]);

  useEffect(() => {
    if (!canvas) {
      return;
    }

    // cursor management
    if (isFreeDrawingState) {
      // clear canvas from previous freeShapes
      clearFreeDrawFromCanvas(canvas);

      canvas.defaultCursor = CANVAS_CURSOR.FREE_DRAWING.DEFAULT;
      canvas.hoverCursor = CANVAS_CURSOR.FREE_DRAWING.HOVER;
      canvas.freeDrawingCursor = CANVAS_CURSOR.FREE_DRAWING.DRAW;
    } else {
      canvas.defaultCursor = CANVAS_CURSOR.DEFAULT.HOVER;
      // when is over shape is grab
      canvas.hoverCursor = CANVAS_CURSOR.DEFAULT.GRAB;
      canvas.freeDrawingCursor = CANVAS_CURSOR.DEFAULT.GRABBING;
    }
  }, [canvas, isFreeDrawingState]);

  return {
    activateWithSelectedTab,
  };
};
